import { useState } from "react";
import { motion } from "framer-motion";
import { CameraIcon, PlusIcon } from "../../utils/icons";
import Button from "../ui/Button";

export default function HairJournal() {
  const [entries, setEntries] = useState([]);
  const [isAddingEntry, setIsAddingEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split("T")[0],
    image: null,
    products: "",
    routine: "",
    notes: "",
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewEntry((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setEntries((prev) => [{ ...newEntry, id: Date.now() }, ...prev]);
    setIsAddingEntry(false);
    setNewEntry({
      date: new Date().toISOString().split("T")[0],
      image: null,
      products: "",
      routine: "",
      notes: "",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-serif font-bold text-primary-900">
          Hair Journal
        </h2>
        <Button onClick={() => setIsAddingEntry(true)}>
          <PlusIcon className="h-5 w-5 mr-2" />
          New Entry
        </Button>
      </div>

      {isAddingEntry && (
        <motion.form
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-lg shadow-md space-y-4"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center justify-center">
            <label className="w-full h-48 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500">
              {newEntry.image ? (
                <img
                  src={newEntry.image}
                  alt="Preview"
                  className="h-full w-full object-cover rounded-lg"
                />
              ) : (
                <div className="text-center">
                  <CameraIcon className="h-12 w-12 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">Add a photo</p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="date"
              value={newEntry.date}
              onChange={(e) =>
                setNewEntry((prev) => ({ ...prev, date: e.target.value }))
              }
              className="rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
            />
            <input
              type="text"
              placeholder="Products Used"
              value={newEntry.products}
              onChange={(e) =>
                setNewEntry((prev) => ({ ...prev, products: e.target.value }))
              }
              className="rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <textarea
            placeholder="Hair Care Routine"
            value={newEntry.routine}
            onChange={(e) =>
              setNewEntry((prev) => ({ ...prev, routine: e.target.value }))
            }
            rows={3}
            className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
          />

          <textarea
            placeholder="Additional Notes"
            value={newEntry.notes}
            onChange={(e) =>
              setNewEntry((prev) => ({ ...prev, notes: e.target.value }))
            }
            rows={2}
            className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
          />

          <div className="flex space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAddingEntry(false)}
            >
              Cancel
            </Button>
            <Button type="submit" fullWidth>
              Save Entry
            </Button>
          </div>
        </motion.form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {entries.map((entry) => (
          <motion.div
            key={entry.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white p-4 rounded-lg shadow-md"
          >
            {entry.image && (
              <img
                src={entry.image}
                alt="Hair Progress"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
            )}
            <div className="space-y-2">
              <p className="text-sm text-gray-500">{entry.date}</p>
              <p className="font-medium">Products: {entry.products}</p>
              <p className="text-sm text-gray-600">{entry.routine}</p>
              {entry.notes && (
                <p className="text-sm text-gray-500 italic">{entry.notes}</p>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
} 